/**
 * Le app Android della famiglia Yuma: dove sta la versione attiva di ciascuna e, per quelle che hanno anche la
 * web app, dove sta e a che punto è. Fonte unica per la pagina «App mobile» del gestionale, la console e il server
 * (`scaricaAppAndroid`, il link stabile dell'APK dietro `/scarica` delle web app, 20/10/2026).
 *
 * ## La web app di un'app, in due passi (20/10/2026)
 * - `online`: il sito della web app risponde (YumaWORK, work.yumaos.com; YumaCALL, call.yumaos.com dal 21/10/2026).
 *   Solo allora il gestionale la nomina.
 * - `conScarica`: quel sito ha la pagina `/scarica` (che sul telefono Android propone l'app o la web app, e sull'iPhone
 *   la web app) e il server il suo `scaricaAppAndroid`. Solo allora il codice QR e il link delle pagine «App per il
 *   telefono» e «Le mie app», della console e di Yuma portano lì invece che all'APK.
 * ⚠️ Si accendono NELLA STESSA pubblicazione che porta online quel sito e quella funzione: accesi prima, i codici QR
 * portano a una pagina che non c'è. Ogni web app della famiglia usa la cornice comune (`AppDellaFamiglia`), che porta
 * sempre `/scarica`: una web app `online` senza `conScarica` è un interruttore dimenticato, e una prova lo vieta.
 * Successo a YumaWORK: pagina online dal 20/10/2026, interruttore acceso solo il 29/10/2026 (TKT-2026-0099, Elena
 * Zurru: «su iphone non funziona», dopo che Yuma le aveva dato il link dell'APK).
 *
 * Senza import: si copia così com'è nel server (`functions/package.json`, `sync:shared`).
 */
export interface WebAppDellApp {
  indirizzo: string;
  online: boolean;
  conScarica: boolean;
}

export const APP_ANDROID: Record<'yumacall' | 'yumawork' | 'yumafly' | 'yumastock', { documentoVersione: string; webApp: WebAppDellApp | null }> = {
  yumacall: { documentoVersione: 'app_config/android_version', webApp: { indirizzo: 'https://call.yumaos.com', online: true, conScarica: true } },
  yumawork: { documentoVersione: 'app_config/yumawork_android_version', webApp: { indirizzo: 'https://work.yumaos.com', online: true, conScarica: true } },
  yumafly: { documentoVersione: 'app_config/yumafly_android_version', webApp: null },
  yumastock: { documentoVersione: 'app_config/yumastock_android_version', webApp: null },
};

export type IdAppAndroid = keyof typeof APP_ANDROID;

export function eAppAndroid(id: string): id is IdAppAndroid {
  return Object.prototype.hasOwnProperty.call(APP_ANDROID, id);
}

/** Il percorso, sul sito di una web app, che porta all'APK attuale (riscrittura verso `scaricaAppAndroid`). */
export const percorsoApk = (id: IdAppAndroid): string => `/scarica/android/${id}`;

/** Il sito del gestionale: ha la stessa riscrittura di `percorsoApk`, quindi porta all'APK di OGNI app, anche senza web app. */
const SITO_DEL_GESTIONALE = 'https://app.yumaos.com';

/**
 * L'indirizzo da mostrare e da mandare per scaricare l'APK di un'app (30/10/2026, Simone: «vedere che c'è scritto
 * Firebase nell'URL»): un nostro indirizzo, che il server (`scaricaAppAndroid`) rimanda al file della versione attiva.
 *
 * ⚠️ MAI l'`apkDownloadUrl` di Storage a una persona (bottone, codice QR, link copiato, risposta di Yuma): porta il nome
 * di Firebase e un codice che cambia a ogni rilascio, quindi un QR stampato o un link mandato su WhatsApp smette di
 * funzionare alla versione dopo («accesso negato»). Quell'indirizzo resta solo alle app, che lo usano per aggiornarsi.
 */
export const indirizzoApk = (id: IdAppAndroid): string => `${SITO_DEL_GESTIONALE}${percorsoApk(id)}`;

/**
 * L'indirizzo della web app, se è online e l'azienda non l'ha spenta (`versioneWebConsentita`, passata da chi conosce
 * l'azienda: di serie permessa); altrimenti `null`.
 */
export function webAppOnline(id: IdAppAndroid, consentitaDallAzienda = true): string | null {
  const w = APP_ANDROID[id].webApp;
  return w && w.online && consentitaDallAzienda ? w.indirizzo : null;
}

/** Le app che hanno una web app online: quelle di cui l'amministratore può permettere o spegnere la versione web. */
export function appConVersioneWeb(): IdAppAndroid[] {
  return (Object.keys(APP_ANDROID) as IdAppAndroid[]).filter((id) => APP_ANDROID[id].webApp?.online === true);
}

/**
 * Il link UNICO da mandare a chi deve installare un'app che ha anche la web app (decisione del 20/10/2026, «quello che
 * fanno i software professionali»): la pagina `/scarica` della web app. `null` finché quella pagina non è online, e per
 * le app solo Android: il loro link resta l'APK.
 */
export function paginaPerScaricare(id: IdAppAndroid, consentitaDallAzienda = true): string | null {
  const w = APP_ANDROID[id].webApp;
  // Versione web spenta dall'azienda: la pagina proporrebbe anche la web app, quindi il link resta l'APK.
  return w && w.online && w.conScarica && consentitaDallAzienda ? `${w.indirizzo}/scarica` : null;
}
